import React, { useState } from 'react';
import { Button, Checkbox, Dropdown, Menu, Modal, Space } from 'antd';
import { DownOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
// Types
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
import { TicketEventStatus, TicketTaxMode } from 'api/graphql-global-types';
// Hooks
import { useTicketSaleStatus } from 'pages/Events/hooks/useTicketSaleStatus';
// Helpers
import {
  canApproveZeroTax,
  canReschedule,
  EventLifecycleAction,
  EVENT_LIFECYCLE_ACTIONS,
  LIFECYCLE_ACTION_LABEL,
  LIFECYCLE_ACTION_TARGET,
} from 'pages/Events/helpers';
import { runTicketingChange } from 'pages/Events/runTicketingChange';
// Components
import RescheduleModal from '../RescheduleModal/RescheduleModal';

type EventDetails = TicketEventDetailsFields;

const PRIMARY_ACTION: Partial<Record<TicketEventStatus, EventLifecycleAction>> =
  {
    [TicketEventStatus.Draft]: 'publish',
    [TicketEventStatus.OnSale]: 'pause',
    [TicketEventStatus.Paused]: 'resume',
    [TicketEventStatus.Closed]: 'reopen',
  };

export const ACTION_CONFIRMATION: Record<
  EventLifecycleAction,
  { title: string; content: string; okText: string; danger?: boolean }
> = {
  publish: {
    title: 'Publish the event?',
    content:
      'Fans can see it and buy tickets right away, within each tier’s sales window.',
    okText: 'Publish',
  },
  resume: {
    title: 'Resume ticket sales?',
    content: 'Buyers can start checkouts again.',
    okText: 'Resume sales',
  },
  reopen: {
    title: 'Reopen ticket sales?',
    content: 'Buyers can start checkouts again until sales close.',
    okText: 'Reopen sales',
  },
  pause: {
    title: 'Pause ticket sales?',
    content:
      'Buyers can’t start new checkouts until sales resume. Tickets already sold stay valid.',
    okText: 'Pause sales',
  },
  close: {
    title: 'Close ticket sales?',
    content:
      'Sales end now. Tickets already sold stay valid and you can reopen sales later.',
    okText: 'Close sales',
  },
  postpone: {
    title: 'Postpone the event?',
    content:
      'Sales stop and door scanners turn tickets away as "event postponed" until you reschedule it. Tickets stay valid for the new date.',
    okText: 'Postpone',
    danger: true,
  },
  cancel: {
    title: 'Cancel the event?',
    content:
      'This can’t be undone. Sales stop for good and door scanners turn every ticket away. Paid orders are not refunded automatically: use Refund all paid orders on the Orders & refunds tab.',
    okText: 'Cancel event',
    danger: true,
  },
};

export const ACTION_SUCCESS: Record<EventLifecycleAction, string> = {
  publish: 'The event is published and on sale',
  pause: 'Ticket sales are paused',
  resume: 'Ticket sales are open again',
  close: 'Ticket sales are closed',
  reopen: 'Ticket sales are open again',
  postpone: 'The event is postponed. Reschedule it once there is a new date.',
  cancel: 'The event is cancelled. Refund paid orders from the Orders tab.',
};

const OPENS_SALES: EventLifecycleAction[] = ['publish', 'resume', 'reopen'];

type LifecycleActionsProps = {
  event: EventDetails;
  onChanged: (action?: EventLifecycleAction) => void;
};

const LifecycleActions = ({
  event,
  onChanged,
}: LifecycleActionsProps): JSX.Element | null => {
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [pendingAction, setPendingAction] =
    useState<EventLifecycleAction | null>(null);
  const [taxConfirmed, setTaxConfirmed] = useState(false);
  const { approveZeroTax, setStatus, busy } = useTicketSaleStatus();

  const actions = EVENT_LIFECYCLE_ACTIONS[event.status];
  const primary = PRIMARY_ACTION[event.status];
  const taxApproved = event.taxMode === TicketTaxMode.ApprovedZero;
  // Only an event that sells tickets needs a tax policy to open sales
  const showApproveTax =
    event.ticketsEnabled && canApproveZeroTax(event.status, event.taxMode);

  const runAction = async (
    action: EventLifecycleAction,
    { withTaxApproval = false } = {}
  ): Promise<void> => {
    const done = await runTicketingChange(
      () =>
        setStatus(event, LIFECYCLE_ACTION_TARGET[action], {
          approveZeroTaxFirst: withTaxApproval,
        }),
      ACTION_SUCCESS[action],
      `Failed to ${LIFECYCLE_ACTION_LABEL[action].toLowerCase()}`
    );

    if (done) {
      onChanged(action);
    } else {
      onChanged();
    }
  };

  const openConfirmation = (action: EventLifecycleAction): void => {
    setTaxConfirmed(false);
    setPendingAction(action);
  };

  const closeConfirmation = (): void => setPendingAction(null);

  const confirmApproveTax = (): void => {
    Modal.confirm({
      title: 'Approve the zero-tax policy?',
      icon: <ExclamationCircleOutlined />,
      content:
        'Tickets for this event are sold with no sales tax added. Opening sales requires a tax policy, and this one can’t be withdrawn.',
      okText: 'Approve',
      onOk: async () => {
        await runTicketingChange(
          () => approveZeroTax(event),
          'The zero-tax policy is approved',
          'Failed to approve the tax policy'
        );
        onChanged();
      },
    });
  };

  if (event.status === TicketEventStatus.Cancelled) {
    return null;
  }

  const secondary = actions.filter((action) => action !== primary);
  const confirmation = pendingAction
    ? ACTION_CONFIRMATION[pendingAction]
    : null;
  const needsTax =
    !!pendingAction &&
    OPENS_SALES.includes(pendingAction) &&
    event.ticketsEnabled &&
    !taxApproved;

  return (
    <>
      <Space wrap>
        {primary && (
          <Button
            type="primary"
            loading={busy}
            onClick={() => openConfirmation(primary)}
          >
            {LIFECYCLE_ACTION_LABEL[primary]}
          </Button>
        )}
        {event.status === TicketEventStatus.Postponed && (
          <Button type="primary" onClick={() => setRescheduleOpen(true)}>
            Reschedule
          </Button>
        )}
        <Dropdown
          trigger={['click']}
          placement="bottomRight"
          overlay={
            <Menu selectable={false}>
              {showApproveTax && (
                <Menu.Item key="approve-tax" onClick={confirmApproveTax}>
                  Approve zero-tax policy
                </Menu.Item>
              )}
              {canReschedule(event.status) &&
                event.status !== TicketEventStatus.Postponed && (
                  <Menu.Item
                    key="reschedule"
                    onClick={() => setRescheduleOpen(true)}
                  >
                    Reschedule
                  </Menu.Item>
                )}
              {secondary.map((action) => (
                <Menu.Item
                  key={action}
                  danger={ACTION_CONFIRMATION[action].danger}
                  onClick={() => openConfirmation(action)}
                >
                  {LIFECYCLE_ACTION_LABEL[action]}
                </Menu.Item>
              ))}
            </Menu>
          }
        >
          <Button disabled={busy}>
            More actions <DownOutlined />
          </Button>
        </Dropdown>
      </Space>

      <Modal
        title={confirmation?.title}
        visible={!!pendingAction}
        onCancel={closeConfirmation}
        onOk={async () => {
          if (pendingAction) {
            await runAction(pendingAction, { withTaxApproval: needsTax });
          }
          closeConfirmation();
        }}
        okText={confirmation?.okText}
        okButtonProps={{
          danger: confirmation?.danger,
          disabled: needsTax && !taxConfirmed,
        }}
        confirmLoading={busy}
        destroyOnClose
      >
        <p>{confirmation?.content}</p>
        {needsTax && (
          <Checkbox
            checked={taxConfirmed}
            onChange={(changeEvent) =>
              setTaxConfirmed(changeEvent.target.checked)
            }
          >
            Approve the zero-tax policy (no sales tax is added to ticket
            prices). Sales can’t open without a tax policy.
          </Checkbox>
        )}
      </Modal>

      <RescheduleModal
        event={event}
        visible={rescheduleOpen}
        onClose={() => setRescheduleOpen(false)}
        onRescheduled={() => {
          setRescheduleOpen(false);
          onChanged();
        }}
      />
    </>
  );
};

export default LifecycleActions;
